export interface BlogPost {
  slug: string;
  title: string;
  date: string;
  category: string;
  tags: string[];
  description: string;
  thumbnail: string;
  featured: boolean;
  content: string;
}

interface Frontmatter {
  [key: string]: string | string[] | boolean;
}

function parseFrontmatter(raw: string): { frontmatter: Frontmatter; content: string } {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { frontmatter: {}, content: raw };

  const frontmatterStr = match[1];
  const content = match[2].trim();
  const frontmatter: Frontmatter = {};

  for (const line of frontmatterStr.split('\n')) {
    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) continue;
    const key = line.slice(0, colonIndex).trim();
    let value = line.slice(colonIndex + 1).trim();

    // Remove quotes
    if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
      value = value.slice(1, -1);
    }

    // Parse arrays
    if (value.startsWith('[') && value.endsWith(']')) {
      const arr = value.slice(1, -1).split(',').map(s => {
        const trimmed = s.trim();
        if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
          return trimmed.slice(1, -1);
        }
        return trimmed;
      });
      frontmatter[key] = arr;
      continue;
    }

    // Parse booleans
    if (value === 'true') { frontmatter[key] = true; continue; }
    if (value === 'false') { frontmatter[key] = false; continue; }

    frontmatter[key] = value;
  }

  return { frontmatter, content };
}

const blogModules = import.meta.glob('/content/blog/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;

let _posts: BlogPost[] | null = null;

export function getAllPosts(): BlogPost[] {
  if (_posts) return _posts;

  _posts = Object.entries(blogModules).map(([path, raw]) => {
    const { frontmatter, content } = parseFrontmatter(raw);
    const filename = path.split('/').pop()?.replace('.md', '') || '';

    return {
      slug: (frontmatter.slug as string) || filename,
      title: (frontmatter.title as string) || filename,
      date: (frontmatter.date as string) || '',
      category: (frontmatter.category as string) || '',
      tags: (frontmatter.tags as string[]) || [],
      description: (frontmatter.description as string) || '',
      thumbnail: (frontmatter.thumbnail as string) || '',
      featured: (frontmatter.featured as boolean) || false,
      content,
    };
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return _posts;
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  return getAllPosts().find(post => post.slug === slug);
}

export function getPostsByCategory(category: string): BlogPost[] {
  if (category === 'all') return getAllPosts();
  return getAllPosts().filter(post => post.category === category);
}
